// Importing a Word document into an HTML policy version.
//
// mammoth converts .docx to semantic HTML in the browser. It needs two things
// from us, both of which the conversion spike showed were necessary:
//
//  - a style map, so Word's Heading styles become h1..h3 (mammoth's default
//    only maps Heading 1 to h1 and so on, which is fine, but we also want a
//    Title style to be the h1);
//  - a document transform for documents that use NO heading styles at all,
//    which is how the existing HR policies were written: "Purpose:" is a bold
//    Normal paragraph. Without help there is no outline. The transform promotes
//    short, all-bold paragraphs to headings. It is a guess, so it is applied
//    only when there is nothing better, and the caller is told it was used so
//    HR can check the result.
//
// The functions operate on mammoth's document model (plain objects), which is
// why they can be unit tested in Node without mammoth or a .docx.

export const IMPORT_STYLE_MAP = [
  "p[style-name='Title'] => h1:fresh",
  "p[style-name='Heading 1'] => h1:fresh",
  "p[style-name='Heading 2'] => h2:fresh",
  "p[style-name='Heading 3'] => h3:fresh",
  "p[style-name='Heading 4'] => h4:fresh",
  "p[style-name='AV Title'] => h1:fresh",
  "p[style-name='AV Section'] => h2:fresh",
];

const HEADING_STYLE = /^(heading\s*[1-6]|title)$/i;
const MAX_HEADING_LENGTH = 80;

const textOf = (el) => {
  if (!el) return '';
  if (el.type === 'text') return el.value || '';
  return (el.children || []).map(textOf).join('');
};

const runsOf = (el, acc = []) => {
  if (!el) return acc;
  if (el.type === 'run') acc.push(el);
  else (el.children || []).forEach((c) => runsOf(c, acc));
  return acc;
};

const paragraphsOf = (el, acc = []) => {
  if (!el) return acc;
  if (el.type === 'paragraph') acc.push(el);
  else (el.children || []).forEach((c) => paragraphsOf(c, acc));
  return acc;
};

/** Does any paragraph in the document use a Word heading (or Title) style? */
export function hasHeadingStyles(document) {
  return paragraphsOf(document).some((p) => {
    const name = p.styleName || '';
    const id = p.styleId || '';
    return HEADING_STYLE.test(name.trim()) || /^(heading[1-6]|title)$/i.test(id.trim());
  });
}

const unbold = (el) => (el.type === 'run'
  ? { ...el, isBold: false }
  : { ...el, children: (el.children || []).map(unbold) });

/**
 * In a document with no heading styles, promote short all-bold paragraphs to
 * headings: the first all-caps one is the title, the rest are sections.
 * Paragraphs inside tables are left alone. A styled document is returned
 * untouched.
 */
export function promoteBoldParagraphs(document) {
  if (hasHeadingStyles(document)) return document;
  let seenTitle = false;
  const visit = (el) => {
    if (!el || typeof el !== 'object') return el;
    // Bold text in a table cell is a label, not a section of the document.
    if (el.type === 'table') return el;
    if (el.type === 'paragraph') {
      const text = textOf(el).trim();
      if (!text || el.numbering || el.styleId || el.styleName) return el;
      const runs = runsOf(el);
      if (runs.length === 0) return el;
      const allBold = runs.every((r) => r.isBold || !textOf(r).trim());
      if (!allBold || text.length > MAX_HEADING_LENGTH) return el;
      const isTitle = !seenTitle && text === text.toUpperCase() && /[A-Z]/.test(text);
      if (isTitle) seenTitle = true;
      return { ...unbold(el), styleName: isTitle ? 'AV Title' : 'AV Section' };
    }
    if (Array.isArray(el.children)) return { ...el, children: el.children.map(visit) };
    return el;
  };
  return visit(document);
}

/**
 * The options to hand mammoth.convertToHtml for this document, plus a flag
 * saying whether the heading heuristic was needed.
 * @param {object} document mammoth's document model (from the transform hook,
 *   or a pre-read of it)
 */
export function buildImportOptions(document) {
  const usedHeuristic = !hasHeadingStyles(document);
  return {
    styleMap: IMPORT_STYLE_MAP,
    transformDocument: usedHeuristic ? promoteBoldParagraphs : undefined,
    usedHeuristic,
  };
}
